import { EditorState } from 'draft-js';
import {
  createContext,
  Dispatch,
  FC,
  SetStateAction,
  useContext,
  useEffect,
  useReducer,
  useState,
} from 'react';
import { update } from 'services/utilsService';
import {
  getArchivedStorageRequestAction,
  getStorageRequestsAction,
} from 'state/actions/storageRequest';
import {
  ACCEPT_STORAGE_REQUEST_FAILURE,
  ACCEPT_STORAGE_REQUEST_REQUEST,
  ACCEPT_STORAGE_REQUEST_SUCCESS,
  ADD_STORAGE_REQUEST,
  ARCHIVE_STORAGE_REQUEST_FAILURE,
  ARCHIVE_STORAGE_REQUEST_REQUEST,
  ARCHIVE_STORAGE_REQUEST_SUCCESS,
  CANCEL_STORAGE_REQUEST_FAILURE,
  CANCEL_STORAGE_REQUEST_REQUEST,
  CANCEL_STORAGE_REQUEST_SUCCESS,
  CLEAR_CREATE_STORAGE_REQUEST_CONTEXT,
  CLEAR_STORAGE_REQUEST_CONTEXT,
  CREATE_STORAGE_REQUEST_FAILURE,
  CREATE_STORAGE_REQUEST_REQUEST,
  CREATE_STORAGE_REQUEST_SUCCESS,
  DECLINE_STORAGE_REQUEST_FAILURE,
  DECLINE_STORAGE_REQUEST_REQUEST,
  DECLINE_STORAGE_REQUEST_SUCCESS,
  GET_ARCHIVED_STORAGE_REQUESTS_FAILURE,
  GET_ARCHIVED_STORAGE_REQUESTS_REQUEST,
  GET_ARCHIVED_STORAGE_REQUESTS_SUCCESS,
  GET_STORAGE_REQUESTS_FAILURE,
  GET_STORAGE_REQUESTS_REQUEST,
  GET_STORAGE_REQUESTS_SUCCESS,
  SET_STORAGE_REQUEST_TO_SEEN_FAILURE,
  SET_STORAGE_REQUEST_TO_SEEN_REQUEST,
  SET_STORAGE_REQUEST_TO_SEEN_SUCCESS,
} from 'state/actionTypes/storageRequestTypes';
import { useUserContext } from '../userContext/userContext';
import {
  StorageRequestAction,
  StorageRequestReducer,
  StorageRequestState,
} from './storageRequest.types';

type StorageRequestContext = {
  storageRequestsState: StorageRequestState;
  textEditorState: EditorState;
  setTextEditorState: Dispatch<SetStateAction<EditorState>>;
  dispatch: Dispatch<StorageRequestAction>;
};
type UseStorageRequestContext = () => StorageRequestContext;

const StorageRequestContext = createContext({} as StorageRequestContext);

const INITIAL_STATE: StorageRequestState = {
  getStorageRequestsRequest: { status: 'Initial' },
  createStorageRequestRequest: { status: 'Initial' },
  archiveStorageRequestRequest: { status: 'Initial' },
  getArchivedStorageRequestsRequest: { status: 'Initial' },
  confirmStorageRequestRequest: { status: 'Initial' },
  cancelStorageRequestRequest: { status: 'Initial' },
  setStorageRequestToSeenRequest: { status: 'Initial' },
  storageRequests: [],
  archivedStorageRequests: [],
};

const storageRequestReducer: StorageRequestReducer = (state, action) => {
  const { type } = action;

  switch (type) {
    case GET_STORAGE_REQUESTS_REQUEST:
      return update(state, {
        getStorageRequestsRequest: { status: 'InProgress' },
      });

    case GET_STORAGE_REQUESTS_SUCCESS:
      return update(state, {
        getStorageRequestsRequest: { status: 'Success' },
        storageRequests: action.payload,
      });

    case GET_STORAGE_REQUESTS_FAILURE:
      return update(state, {
        getStorageRequestsRequest: { status: 'Error', error: action.payload },
        storageRequests: [],
      });

    case CREATE_STORAGE_REQUEST_REQUEST:
      return update(state, {
        createStorageRequestRequest: { status: 'InProgress' },
      });

    case CREATE_STORAGE_REQUEST_SUCCESS:
      return update(state, {
        createStorageRequestRequest: { status: 'Success' },
        storageRequests: [action.payload, ...state.storageRequests],
      });

    case CREATE_STORAGE_REQUEST_FAILURE:
      return update(state, {
        createStorageRequestRequest: { status: 'Error', error: action.payload },
        storageRequests: [],
      });

    case ARCHIVE_STORAGE_REQUEST_REQUEST:
      return update(state, {
        archiveStorageRequestRequest: { status: 'InProgress' },
      });

    case ARCHIVE_STORAGE_REQUEST_SUCCESS:
      return update(state, {
        archiveStorageRequestRequest: { status: 'Success' },
        archivedStorageRequests: [action.payload, ...state.archivedStorageRequests],
        storageRequests: state.storageRequests.filter(
          (request) => request.id !== action.payload.id,
        ),
      });

    case ARCHIVE_STORAGE_REQUEST_FAILURE:
      return update(state, {
        archiveStorageRequestRequest: { status: 'Error', error: action.payload },
      });

    case GET_ARCHIVED_STORAGE_REQUESTS_REQUEST:
      return update(state, {
        getArchivedStorageRequestsRequest: { status: 'InProgress' },
      });

    case GET_ARCHIVED_STORAGE_REQUESTS_SUCCESS:
      return update(state, {
        getArchivedStorageRequestsRequest: { status: 'Success' },
        archivedStorageRequests: action.payload,
      });

    case GET_ARCHIVED_STORAGE_REQUESTS_FAILURE:
      return update(state, {
        getArchivedStorageRequestsRequest: { status: 'Error', error: action.payload },
        archivedStorageRequests: [],
      });

    case ACCEPT_STORAGE_REQUEST_REQUEST:
      return update(state, {
        confirmStorageRequestRequest: { status: 'InProgress' },
      });

    case ACCEPT_STORAGE_REQUEST_SUCCESS:
      return update(state, {
        confirmStorageRequestRequest: { status: 'Success' },
        storageRequests: [
          action.payload,
          ...state.storageRequests.filter((request) => request.id !== action.payload.id),
        ],
      });

    case ACCEPT_STORAGE_REQUEST_FAILURE:
      return update(state, {
        confirmStorageRequestRequest: { status: 'Error', error: action.payload },
      });

    case DECLINE_STORAGE_REQUEST_REQUEST:
      return update(state, {
        confirmStorageRequestRequest: { status: 'InProgress' },
      });

    case DECLINE_STORAGE_REQUEST_SUCCESS:
      return update(state, {
        confirmStorageRequestRequest: { status: 'Success' },
        archivedStorageRequests: [action.payload, ...state.archivedStorageRequests],
        storageRequests: state.storageRequests.filter(
          (request) => request.id !== action.payload.id,
        ),
      });

    case DECLINE_STORAGE_REQUEST_FAILURE:
      return update(state, {
        confirmStorageRequestRequest: { status: 'Error', error: action.payload },
      });

    case CANCEL_STORAGE_REQUEST_REQUEST:
      return update(state, {
        cancelStorageRequestRequest: { status: 'InProgress' },
      });

    case CANCEL_STORAGE_REQUEST_SUCCESS:
      return update(state, {
        cancelStorageRequestRequest: { status: 'Success' },
        storageRequests: action.payload.isArchived
          ? state.storageRequests
          : state.storageRequests.filter((request) => request.id !== action.payload.id),
        archivedStorageRequests: action.payload.isArchived
          ? state.archivedStorageRequests.filter(
              (request) => request.id !== action.payload.id,
            )
          : state.archivedStorageRequests,
      });

    case CANCEL_STORAGE_REQUEST_FAILURE:
      return update(state, {
        cancelStorageRequestRequest: { status: 'Error', error: action.payload },
      });

    case SET_STORAGE_REQUEST_TO_SEEN_REQUEST:
      return update(state, {
        setStorageRequestToSeenRequest: { status: 'InProgress' },
      });

    case SET_STORAGE_REQUEST_TO_SEEN_SUCCESS:
      return update(state, {
        setStorageRequestToSeenRequest: { status: 'Success' },
        storageRequests: state.storageRequests.map((sr) => ({
          ...sr,
          isSeen:
            sr.conversationSid === action.payload.conversationSid ? true : sr.isSeen,
        })),
      });

    case SET_STORAGE_REQUEST_TO_SEEN_FAILURE:
      return update(state, {
        setStorageRequestToSeenRequest: { status: 'Error', error: action.payload },
        storageRequests: [],
      });

    case ADD_STORAGE_REQUEST:
      return update(state, {
        storageRequests: state.storageRequests.some((sr) => sr.id === action.payload.id)
          ? state.storageRequests
          : [action.payload, ...state.storageRequests],
      });

    case CLEAR_STORAGE_REQUEST_CONTEXT:
      return INITIAL_STATE;

    case CLEAR_CREATE_STORAGE_REQUEST_CONTEXT:
      return update(state, {
        createStorageRequestRequest: { status: 'Initial' },
      });

    default:
      return state;
  }
};

export const StorageRequestProvider: FC = ({ children }) => {
  const [storageRequestsState, dispatch] = useReducer(
    storageRequestReducer,
    INITIAL_STATE,
  );
  const {
    user: { userData },
  } = useUserContext();

  useEffect(() => {
    if (userData && storageRequestsState.getStorageRequestsRequest.status === 'Initial') {
      getStorageRequestsAction(dispatch);
    }
  }, [storageRequestsState.getStorageRequestsRequest.status, userData]);

  useEffect(() => {
    if (
      userData &&
      storageRequestsState.getArchivedStorageRequestsRequest.status === 'Initial'
    ) {
      getArchivedStorageRequestAction(dispatch);
    }
  }, [storageRequestsState.getArchivedStorageRequestsRequest.status, userData]);

  useEffect(() => () => dispatch({ type: CLEAR_STORAGE_REQUEST_CONTEXT }), [dispatch]);

  const [textEditorState, setTextEditorState] = useState(() => EditorState.createEmpty());

  return (
    <StorageRequestContext.Provider
      value={{
        dispatch,
        storageRequestsState,
        textEditorState,
        setTextEditorState,
      }}
    >
      {children}
    </StorageRequestContext.Provider>
  );
};

export const useStorageRequestContext: UseStorageRequestContext = () =>
  useContext(StorageRequestContext);
